import React from 'react';
import { StudyPlanTopic } from '@lf/ui-primitives';
import { donutChart } from '../../api/donutChart';

const StudyPlanLesson = ({
    lesson,
    lessonIndex,
    currentLessonIndex,
    currentTopicIndex,
    completedVideosNum,
    completedTopicIds,
    onLessonClick,
    onTopicClick,
    isLastLesson
}) => {
    const isReadOnly = onLessonClick === null;
    const isCurrent = !isReadOnly && lessonIndex === currentLessonIndex;

    const completedTopicIdSet = new Set(completedTopicIds || []);
    const lessonTopics = lesson.topics || [];
    const isCompleted = !isReadOnly
        && lessonTopics.length > 0
        && lessonTopics.every(t => completedTopicIdSet.has(t.id));

    const badgeBase = 'w-12 h-12 rounded-full flex items-center justify-center font-bold text-base leading-none';
    let badgeStyle;
    if (isCurrent) {
        badgeStyle = 'bg-primary-600 text-white';
    } else if (isCompleted) {
        badgeStyle = 'bg-emerald-500 text-white cursor-pointer';
    } else {
        badgeStyle = `bg-white border border-black/15 text-darkrock/60 ${isReadOnly ? '' : 'cursor-pointer'}`;
    }
    const badge = (
        <div
            className={`${badgeBase} ${badgeStyle}`}
            onClick={() => onLessonClick && onLessonClick(lessonIndex, false)}
            aria-label={isCompleted ? `Lesson ${lessonIndex + 1} completed` : undefined}
        >
            {isCompleted && !isCurrent ? '✓' : lessonIndex + 1}
        </div>
    );

    const connectorColor = isCompleted ? 'bg-emerald-500/40' : isCurrent ? 'bg-primary-600/40' : 'bg-black/10';

    let nextLessonEnabled = false;
    let nextLessonStyle = '';
    if (isCurrent && !isLastLesson) {
        nextLessonEnabled = completedVideosNum >= lesson.num_videos;
        nextLessonStyle = nextLessonEnabled ? 'bg-primary-600' : 'bg-gray-400';
    }

    const containerBg = isCurrent
        ? 'bg-pastelblue-light ring-1 ring-primary-600/30'
        : '';

    return (
        <div className={`flex items-start mb-4 rounded-xl px-3 py-3 ${containerBg}`}>
            <div className="mr-4 flex flex-col items-center">
                {badge}
                {!isLastLesson && (
                    <div className={`w-0.5 h-6 mt-1 ${connectorColor}`} />
                )}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <div className={`font-semibold ${isCompleted ? 'text-darkrock/50 line-through' : 'text-darkrock'}`}>
                        {lesson.title}
                    </div>
                    {isCurrent && (
                        <span className="px-2 py-0.5 rounded-full bg-primary-600 text-white text-[10px] font-bold tracking-wide">
                            CURRENT
                        </span>
                    )}
                </div>
                {!isCompleted && lesson.explanation && (
                    <div className="text-darkrock/80">{lesson.explanation}</div>
                )}

                {lessonTopics.map((topic, topicIndex) => {
                    const isTopicCurrent = isCurrent && topicIndex === currentTopicIndex;
                    const isTopicCompleted = completedTopicIdSet.has(topic.id);
                    const clickable = !isReadOnly && isCurrent;
                    return (
                        <div
                            key={`lesson1:${topic.id}`}
                            className={clickable ? 'cursor-pointer' : ''}
                            onClick={() => clickable && onTopicClick && onTopicClick(lessonIndex, topicIndex)}
                        >
                            <StudyPlanTopic
                                topic={topic}
                                index={topicIndex}
                                isCompleted={isTopicCompleted}
                                isCurrent={isTopicCurrent}
                            />
                        </div>
                    );
                })}

                <div className="flex flex-col items-start sm:flex-row sm:items-center">
                    {isCurrent && (
                        <>
                            <div className="flex items-center mr-3">
                                <button
                                    className={`${nextLessonStyle} text-white py-2 px-4 rounded mt-2`}
                                    disabled={!nextLessonEnabled}
                                    onClick={() => onLessonClick(lessonIndex + 1, false)}
                                >
                                    Next lesson
                                </button>
                            </div>
                            <div className="flex items-center mt-2">
                                {donutChart({
                                    percentage: Math.min(100, Math.floor(completedVideosNum / lesson.num_videos * 100))
                                })}
                                <div className="ml-1">{completedVideosNum} video(s) completed</div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default StudyPlanLesson;
